import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Sidebar } from '../../shared/sidebar/sidebar';
import { UserService } from '../../core/services/user.service';
import { Coach, CreateCoachRequest } from '../../core/models/user.model';

@Component({
  selector: 'app-trainers',
  imports: [CommonModule, FormsModule, Sidebar],
  templateUrl: './trainers.html',
  styleUrl: './trainers.scss',
})
export class Trainers implements OnInit {
  searchQuery: string = '';
  currentPage: number = 1;
  itemsPerPage: number = 25;
  totalPages: number = 1;
  isLoading: boolean = false;

  trainers: Coach[] = [];
  filteredTrainers: Coach[] = [];

  // Create modal
  showCreateModal: boolean = false;
  isCreating: boolean = false;
  createError: string = '';
  newCoach: CreateCoachRequest = {
    name: '',
    phoneNumber: '',
    password: '',
    email: '',
  };

  constructor(private userService: UserService) {}

  ngOnInit() {
    this.loadCoaches();
  }

  loadCoaches() {
    this.isLoading = true;
    this.userService.getCoaches().subscribe({
      next: (response) => {
        this.isLoading = false;
        if (response.success) {
          this.trainers = response.data;
          this.filteredTrainers = this.trainers;
          this.calculatePagination();
        }
      },
      error: (error) => {
        this.isLoading = false;
        console.error('Error loading coaches:', error);
      }
    });
  }

  calculatePagination() {
    this.totalPages = Math.ceil(this.filteredTrainers.length / this.itemsPerPage);
  }

  get paginatedTrainers() {
    const start = (this.currentPage - 1) * this.itemsPerPage;
    const end = start + this.itemsPerPage;
    return this.filteredTrainers.slice(start, end);
  }

  onSearch() {
    if (!this.searchQuery.trim()) {
      this.filteredTrainers = this.trainers;
    } else {
      this.filteredTrainers = this.trainers.filter(trainer =>
        trainer.name.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
        (trainer.email && trainer.email.toLowerCase().includes(this.searchQuery.toLowerCase())) ||
        trainer.phoneNumber.toLowerCase().includes(this.searchQuery.toLowerCase())
      );
    }
    this.currentPage = 1;
    this.calculatePagination();
  }

  goToPage(page: number) {
    if (page >= 1 && page <= this.totalPages && page !== this.currentPage) {
      this.currentPage = page;
    }
  }

  nextPage() {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
    }
  }

  previousPage() {
    if (this.currentPage > 1) {
      this.currentPage--;
    }
  }

  getPageNumbers(): (number | string)[] {
    const pages: (number | string)[] = [];
    const maxVisible = 5;

    if (this.totalPages <= maxVisible + 2) {
      for (let i = 1; i <= this.totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);

      let start = Math.max(2, this.currentPage - 1);
      let end = Math.min(this.totalPages - 1, this.currentPage + 1);

      if (this.currentPage <= 3) {
        start = 2;
        end = 4;
      }

      if (this.currentPage >= this.totalPages - 2) {
        start = this.totalPages - 3;
        end = this.totalPages - 1;
      }

      if (start > 2) {
        pages.push('...');
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (end < this.totalPages - 1) {
        pages.push('...');
      }

      pages.push(this.totalPages);
    }

    return pages;
  }

  getAvatar(name: string): string {
    const colors = ['0B8DD5', 'FF8B3D', '22C55E', 'F59E0B', 'EF4444', '8B5CF6', '06B6D4'];
    const colorIndex = name.length % colors.length;
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=${colors[colorIndex]}&color=fff`;
  }

  // Create modal methods
  openCreateModal() {
    this.showCreateModal = true;
    this.createError = '';
    this.newCoach = { name: '', phoneNumber: '', password: '', email: '' };
  }

  closeCreateModal() {
    this.showCreateModal = false;
    this.createError = '';
  }

  submitCreateCoach() {
    if (!this.newCoach.name.trim() || !this.newCoach.phoneNumber.trim() || !this.newCoach.password.trim()) {
      this.createError = 'Name, phone number, and password are required.';
      return;
    }

    this.isCreating = true;
    this.createError = '';

    const request: CreateCoachRequest = {
      name: this.newCoach.name.trim(),
      phoneNumber: this.newCoach.phoneNumber.trim(),
      password: this.newCoach.password.trim(),
    };
    if (this.newCoach.email?.trim()) {
      request.email = this.newCoach.email.trim();
    }

    this.userService.createCoach(request).subscribe({
      next: (response) => {
        this.isCreating = false;
        if (response.success) {
          this.closeCreateModal();
          this.loadCoaches();
        } else {
          this.createError = response.message || 'Failed to create coach.';
        }
      },
      error: (error) => {
        this.isCreating = false;
        this.createError = error.error?.message || 'An error occurred while creating the coach.';
      }
    });
  }
}
